"use client"

import { useQuery } from "@tanstack/react-query"

import type { MessageAgentWithChannels } from "@/hooks/agents/messaging/use-agents"
import {
  getShopifyOnboardingStorefrontAction,
  getSignupOnboardingAction,
  getSignupOnboardingMessagingAgentAction,
  getSignupOnboardingEmailAgentAction,
  getSignupOnboardingAgentSummariesAction,
  type SignupOnboardingAgentSummary,
  type SignupOnboardingView,
} from "@/server-actions/signup-onboarding"
import type { ShopifyStorefrontProbe } from "@/lib/onboarding/shopify-storefront-probe"

export const signupOnboardingQueryKey = ["signup-onboarding"] as const
export const signupOnboardingAgentQueryKey = (clientId: string) => [
  ...signupOnboardingQueryKey,
  "agent",
  clientId,
] as const
export const signupOnboardingAgentSummariesQueryKey = [
  ...signupOnboardingQueryKey,
  "agent-summaries",
] as const

export function useSignupOnboardingQuery(enabled = true) {
  return useQuery<SignupOnboardingView>({
    queryKey: signupOnboardingQueryKey,
    enabled,
    queryFn: async () => {
      const result = await getSignupOnboardingAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export const shopifyOnboardingStorefrontQueryKey = [
  ...signupOnboardingQueryKey,
  "shopify-storefront",
] as const

export function useShopifyOnboardingStorefrontQuery(enabled = true) {
  return useQuery<ShopifyStorefrontProbe>({
    queryKey: shopifyOnboardingStorefrontQueryKey,
    enabled,
    staleTime: 0,
    queryFn: async () => {
      const result = await getShopifyOnboardingStorefrontAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useSignupOnboardingMessagingAgentQuery(
  clientId: string | null,
  enabled = true,
) {
  return useQuery<MessageAgentWithChannels | null>({
    queryKey: signupOnboardingAgentQueryKey(clientId ?? "none"),
    enabled: enabled && Boolean(clientId),
    queryFn: async () => {
      if (!clientId) return null
      const result = await getSignupOnboardingMessagingAgentAction({
        client_id: clientId,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.agent as unknown as MessageAgentWithChannels | null
    },
  })
}

export function useSignupOnboardingEmailAgentQuery(clientId: string | null, enabled = true) {
  return useQuery<Record<string, unknown> | null>({
    queryKey: [...signupOnboardingAgentQueryKey(clientId ?? "none"), "email"],
    enabled: enabled && Boolean(clientId),
    queryFn: async () => {
      if (!clientId) return null
      const result = await getSignupOnboardingEmailAgentAction({ client_id: clientId })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.agent
    },
  })
}

export function useSignupOnboardingAgentSummariesQuery(enabled = true) {
  return useQuery<SignupOnboardingAgentSummary[]>({
    queryKey: signupOnboardingAgentSummariesQueryKey,
    enabled,
    queryFn: async () => {
      const result = await getSignupOnboardingAgentSummariesAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result.agents
    },
  })
}
